import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, combineLatest } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { VersionsViewService } from '@app/services/app/versions.service';
import { AppRouterService } from '@app/services/app/router.service';
import { AppService } from '@app/services/app/app.service';

import * as Objects from '@core/objects';
import { AccountSession } from '@core/objects/account-session';
import { TitleService } from '@core/services/title.service';
import { UrlService } from '@core/services/url/url.service';
import { VersionsService } from '@core/services/versions/versions.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { FileDownloadService } from '@core/services/download/file-download.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';

import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';



@Component({
    selector: 'versions-related-file-preview',
    templateUrl: './related-file-preview.component.html',
    styleUrls: ['./related-file-preview.component.scss']
})
export class VersionsRelatedFilePreviewComponent implements OnInit, OnDestroy {
    protected readonly PLUGIN_SEND_FILES = 'share_transfer';
    protected readonly APP_FILECOMPARISON = 'filecomparison';
    protected readonly ACCESS_GLOBAL_XRAY = 'access_global_xray';
    protected readonly PLUGIN_APOLLO = 'apollo'; // transitional: also gate "Compare" on this until apollo is fully retired

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_ERROR = 2;
    public readonly STATUS_NOT_FOUND= 3;
    public readonly STATUS_NOT_ALLOWED= 4;

    public version: Objects.Version = null;
    public versionRelated: Objects.Version = null;
    public file: Objects.AmsFile = null;
    public pluginsAccess: Array<string> = [];
    public appsAccess: Array<string> = [];
    public accessGlobalXray: boolean = true;
    public originProject: Objects.Request = null;

    public context: string = '';
    private relatedId: number = null;
    private fileId: number = null;
    public breadcrumbs: any = [
    ];

    public status:number = this.STATUS_LOADING;    
    public menu: Array<ThreeDotsMenuItem> = [{
        title: "Download File",
        key: "download",
        icon: "download",
        order: 1
    }];


    protected menuSendFiles: ThreeDotsMenuItem = {
        title: "Send",
        key: "send-files",
        icon: "send-files",
        order: 2
    };
    protected menuCompare: ThreeDotsMenuItem = {
        title: "Compare",
        key: "compare",
        icon: "compare",
        order: 3
    };
    protected menuComparison: ThreeDotsMenuItem = {
        title: "NEW Filecomparison",
        htmltitle: "<strong>NEW</strong> Filecomparison",
        key: "comparison",
        icon: "compare",
        order: 4
    };

    protected subs = new Subscription();

    constructor(private title: TitleService,
        private app: AppService,
        private urlSrv: UrlService,
        private appRouter: AppRouterService,
        private activeRoute: ActivatedRoute,
        private notifSrv: NotificationsService,
        private viewVersionsSrv: VersionsViewService,
        private versionsSrv: VersionsService,
        private fileDownloadSrv: FileDownloadService,
        private signatureManager: FileSignatureManagerService) { }

    ngOnInit() {
        this.setDefaultBredcrumb();
        this.title.set("Preview File... - Artwork...");

        this.subs.add(
            this.activeRoute.params.subscribe((params: any) => {
                console.debug(" RELATED FILE PREVIEW params: ",params)
                if(params.fid)
                    this.fileId = +params.fid;
                if(params.rvid){
                    this.relatedId = +params.rvid;
                }
            })
        );

        // get the version
        this.subs.add(
            this.viewVersionsSrv.selectVersion().subscribe((version: Objects.Version) =>
            {
                if(version != null){
                    this.version = version;
                    // load realted all versions for the version in store
                    this.viewVersionsSrv.loadRelated(this.version.labels.map((l: Objects.Label) => l.id).sort((a, b) => a - b).join('-'));
                }
            })
        );

        this.subs.add(
            this.viewVersionsSrv.selectProject().subscribe((projectOrigin: Objects.Request) => {
                this.originProject = projectOrigin;
            })
        );

        this.subs.add(
            this.viewVersionsSrv.selectRelatedVersions().subscribe((relateds: Objects.Version[]) => {
                if(relateds != null && this.relatedId != null){
                    let vr = relateds.filter((v: Objects.Version) => v.id == this.relatedId);
                    if (vr.length > 0){
                        this.versionRelated = vr[0];
                        this.subs.add(
                            this.versionsSrv.getAssets$(this.versionRelated).subscribe((files: Array<Objects.AmsFile>) => {
                                if(files.length > 0 && this.fileId != null){
                                    let match = files.filter((f: Objects.AmsFile) => {return f.id == this.fileId;});
                                    if (match.length > 0 ){
                                        this.file = match[0];
                                    } else {
                                        this.status = this.STATUS_NOT_FOUND;
                                    }
                                    this.initView();
                                }
                            })
                        );
                    }
                }

                this.initView();
            })
        );

        this.subs.add(
            combineLatest([this.app.getSession(), this.app.getAccountSession()]).subscribe( ([session, accountSession]: [any, AccountSession]) => {
                this.pluginsAccess = session.options.plugins;
                // apps list now comes from the accounts-api session; fall back to the legacy session field
                // in case the (separate, parallel) accounts-api call hasn't resolved yet
                this.appsAccess = accountSession?.current?.apps ?? session.options.organization.apps;
                this.accessGlobalXray = session.options.access?.hasOwnProperty(this.ACCESS_GLOBAL_XRAY) ? session.options.access[this.ACCESS_GLOBAL_XRAY] : true;
                this.updateMenu(this.menuSendFiles,this.checkPluginAccess(this.PLUGIN_SEND_FILES))
                this.updateMenu(this.menuCompare,this.checkCompareAccess());
                this.updateMenu(this.menuComparison,this.checkFilecomparisonAccess());
            })
        );
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    onMenuClick(menu: AmsFilesListItemMenuClick) {
        switch(menu.key) {
            case "download-comments": 
                // manage here the download with comments starting notification action to download it..
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILE_ANNOTATIONS_START, {id: this.version.id, details: { url: "/versions/"+this.version.id+"/feedbacks", amsfile: menu.file}});
                break;
            case "download":
                this.handleFileDownload(menu.file);
                break;
            case "send-files":
                if (this.checkPluginAccess(this.PLUGIN_SEND_FILES) && this.originProject != null){
                        this.appRouter.navigate('/p/transfer/'+this.originProject.id, { queryParams: { file: +menu.file.id, version: this.version.id} });
                }
                break;
            case "compare":
                if (this.originProject != null)
                    this.appRouter.navigate('/p/apollo/request/'+this.originProject.id);
                break;
            case "comparison":
                if (this.originProject != null)
                    this.appRouter.navigate('/filecomparison/project/'+this.originProject.id+'/artwork/'+this.versionRelated.id+'/file/'+menu.file.uuid);
                break;
        }
    }

    /**
     * Handle action download click from file preview component
     * Delegates to handleFileDownload for consistent download logic
     */
    onActionDownloadClick(file: Objects.AmsFile) {
        this.handleFileDownload(file);
    }

    /**
     * Handle file download with signature expiration check
     * If signature is expired, directly refresh assets for the related version
     */
    private handleFileDownload(file: Objects.AmsFile) {
        if (file && file.access && this.signatureManager.isSignatureExpired(file)) {
            // Signature expired - get fresh assets directly from the related version
            // This avoids redundant store broadcasts to multiple subscribers
            this.subs.add(
                this.versionsSrv.getAssets$(this.versionRelated).subscribe((assets: Objects.AmsFile[]) => {
                    const updatedFile = assets.find((f: Objects.AmsFile) => this.filesMatch(f, file));
                    if (updatedFile && !this.signatureManager.isSignatureExpired(updatedFile)) {
                        // Fresh signature obtained, download now
                        this.fileDownloadSrv.downloadVersionAsset(updatedFile);
                    } else if (updatedFile) {
                        // Still expired, fallback to legacy
                        window.location.href = this.urlSrv.build('/files/'+updatedFile.id);
                    } else {
                        // File not found, fallback
                        window.location.href = this.urlSrv.build('/files/'+file.id);
                    }
                })
            );
        } else if (file && file.access) {
            // Signature is valid, use the download service
            this.fileDownloadSrv.downloadVersionAsset(file);
        } else {
            // No signature, fall back to legacy download
            window.location.href = this.urlSrv.build('/files/'+file.id);
        }
    }

    /**
     * Helper method to match files by UUID or ID
     * Prefers UUID if both exist for future-proof matching
     */
    private filesMatch(file: Objects.AmsFile, compareWith: Objects.AmsFile | number): boolean {
        if (typeof compareWith === 'number') {
            return file.id === compareWith;
        }
        
        // If both have uuid, use UUID comparison
        if (file.uuid && compareWith.uuid) {
            return file.uuid === compareWith.uuid;
        }
        
        // If comparing with a number (ID), use ID comparison
        return file.id === compareWith.id;
    }

    protected checkPluginAccess(plugin: string){
        return this.pluginsAccess.some((pa) => pa == plugin);
    }

    protected checkAppAccess(app: string){
        return this.appsAccess.some((a) => a == app);
    }

    protected checkCompareAccess(): boolean {
        return this.accessGlobalXray && this.checkPluginAccess(this.PLUGIN_APOLLO);
    }

    protected checkFilecomparisonAccess(): boolean {
        return this.accessGlobalXray && this.checkAppAccess(this.APP_FILECOMPARISON);
    }

    // this function check either remove or add item. based on subscribed events
    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    private initView() {
        if(this.version == null || this.versionRelated == null || this.file == null)
            return;

        this.title.set("Preview File "+this.file.name+ " - Artwork #"+ this.versionRelated.id);
        this.updateBreadcrumbs();
        this.status = this.STATUS_DONE;
    }

    protected updateBreadcrumbs(){ // update intermediate breadcrumb depending on context
        switch (this.context) {
            case "other":
                break;
            default:
                this.breadcrumbs[0] = {link: "/versions/"+this.version.id,
                    title: 'Artwork v.'+this.version.version+' <small>(#'+this.version.id+')</small>'};
                this.breadcrumbs[1] = {link: "/versions/"+this.version.id+"/related",
                    title: 'OTHER ARTWORKS OF v.'+this.version.version };
                break;
        }
    }    

    protected setDefaultBredcrumb(){
        this.breadcrumbs[0] = {link: '',
                            title: 'Artwork v. ...  <small>(# .  .  . )</small>'};
        this.breadcrumbs[1] = {link: "",
                            title: 'OTHER ARTWORKS OF v. ...' };
    }

}
